import { useState } from 'react';
import {
  Edit,
  Trash2,
  FileText,
  File,
  CheckCircle,
  Banknote,
  CreditCard,
  Building2,
  Shield,
  HelpCircle,
  Percent,
} from 'lucide-react';
import { PaymentImagesSection } from './PaymentImagesSection';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import type { JobPayment } from '../types/jobPayment';

interface PaymentListTableProps {
  jobId: number;
  payments: JobPayment[];
  onEdit: (payment: JobPayment) => void;
  onDelete: (paymentId: number) => void;
  onClear: (paymentId: number) => void;
  isDeleting?: boolean;
  isClearing?: boolean;
}

/**
 * Get icon for payment type
 */
function getPaymentTypeIcon(typeName: string) {
  const iconClass = 'h-4 w-4';
  switch (typeName.toLowerCase()) {
    case 'check':
      return <Banknote className={iconClass} />;
    case 'cash':
      return <Banknote className={iconClass} />;
    case 'credit card':
      return <CreditCard className={iconClass} />;
    case 'wire transfer':
      return <Building2 className={iconClass} />;
    case 'insurance check':
      return <Shield className={iconClass} />;
    default:
      return <HelpCircle className={iconClass} />;
  }
}

export function PaymentListTable({
  jobId,
  payments,
  onEdit,
  onDelete,
  onClear,
  isDeleting,
  isClearing,
}: PaymentListTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<number | null>(null);
  const [paymentToClear, setPaymentToClear] = useState<number | null>(null);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedPaymentForDocuments, setSelectedPaymentForDocuments] =
    useState<JobPayment | null>(null);

  const handleOpenDocuments = (payment: JobPayment) => {
    setSelectedPaymentForDocuments(payment);
    setDocumentsDialogOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDeleteClick = (paymentId: number) => {
    setPaymentToDelete(paymentId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (paymentToDelete !== null) {
      onDelete(paymentToDelete);
      setDeleteDialogOpen(false);
      setPaymentToDelete(null);
    }
  };

  const handleClearClick = (paymentId: number) => {
    setPaymentToClear(paymentId);
    setClearDialogOpen(true);
  };

  const handleClearConfirm = () => {
    if (paymentToClear !== null) {
      onClear(paymentToClear);
      setClearDialogOpen(false);
      setPaymentToClear(null);
    }
  };

  // Sort payments by date descending (most recent first)
  const sortedPayments = [...payments].sort((a, b) => {
    return new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime();
  });

  // Calculate total
  const total = payments.reduce((sum, payment) => sum + payment.amount, 0);

  if (payments.length === 0) {
    return (
      <div className="bg-white rounded-[20px] border border-[#d9d9d9] p-8 text-center">
        <p className="text-gray-500">No payments recorded yet.</p>
        <p className="text-sm text-gray-400 mt-1">
          Click &quot;Add Payment&quot; to record your first payment.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-[20px] border border-[#d9d9d9] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Date</TableHead>
              <TableHead className="w-[140px]">Type</TableHead>
              <TableHead className="w-[120px]">Reference #</TableHead>
              <TableHead className="text-right w-[130px]">Amount</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="w-[60px] text-center">Interest</TableHead>
              <TableHead className="w-[80px] text-center">Images</TableHead>
              <TableHead className="w-[140px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedPayments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell className="text-sm">
                  {formatDate(payment.paymentDate)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getPaymentTypeIcon(payment.paymentTypeName)}
                    <span className="text-sm">{payment.paymentTypeName}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-gray-600">
                  {payment.referenceNumber || '-'}
                </TableCell>
                <TableCell className="text-right text-sm font-medium">
                  {formatCurrency(payment.amount)}
                </TableCell>
                <TableCell>
                  {payment.isPending ? (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Pending
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Received
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {payment.isInterest && (
                    <Percent className="h-4 w-4 text-blue-500 mx-auto" />
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {payment.imageCount > 0 ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDocuments(payment)}
                      className="h-8 w-8 p-0"
                      aria-label={`View ${payment.imageCount} document${payment.imageCount !== 1 ? 's' : ''} for payment`}
                    >
                      <FileText className="h-5 w-5 text-[#0A0A9E]" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDocuments(payment)}
                      className="h-8 w-8 p-0"
                      aria-label="Add documents for payment"
                    >
                      <File className="h-5 w-5 text-gray-300" />
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {payment.isPending && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleClearClick(payment.id)}
                        className="h-8 w-8 p-0"
                        aria-label="Mark as cleared"
                        disabled={isClearing}
                        title="Mark as cleared"
                      >
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(payment)}
                      className="h-8 w-8 p-0"
                      aria-label="Edit payment"
                    >
                      <Edit className="h-4 w-4 text-[#0A0A9E]" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(payment.id)}
                      className="h-8 w-8 p-0"
                      aria-label="Delete payment"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {/* Summary row */}
            <TableRow className="bg-gray-50 font-semibold">
              <TableCell colSpan={3} className="text-right">
                Total
              </TableCell>
              <TableCell className="text-right">{formatCurrency(total)}</TableCell>
              <TableCell colSpan={4}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Payment"
        message="Are you sure you want to delete this payment? This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />

      {/* Clear Confirmation Dialog */}
      <ConfirmDialog
        open={clearDialogOpen}
        onOpenChange={setClearDialogOpen}
        title="Mark Payment as Cleared"
        message="Mark this payment as cleared? This will change the status from Pending to Received."
        confirmLabel="Mark Cleared"
        variant="success"
        onConfirm={handleClearConfirm}
      />

      {/* Documents Dialog */}
      <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Payment Documents
              {selectedPaymentForDocuments &&
                ` - ${selectedPaymentForDocuments.paymentTypeName} ${formatCurrency(selectedPaymentForDocuments.amount)}`}
            </DialogTitle>
          </DialogHeader>
          {selectedPaymentForDocuments && (
            <PaymentImagesSection
              jobId={jobId}
              paymentId={selectedPaymentForDocuments.id}
              canDelete={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
